import "../../../fonts/flag-icons.css";

// jsPDF's addImage only embeds raster formats (PNG/JPEG/WEBP), not SVG, so
// the country flags (currently CSS background-image SVGs, see
// flag-icons.css / TournamentPrintableTable.js's CountryFlag) need
// rasterizing before they can go on a vector page. A dynamic
// `import()`/`require()` over the whole flags/ folder was already tried
// for the live CountryFlag component and "returned garbage instead of a
// URL in this webpack setup" (see that component's own comment) - instead
// of retrying that, this reads the URL webpack already resolved into
// flag-icons.css itself: mount a throwaway `.fi .fi-<code>` element and
// read its computed background-image, the same CSS the live component
// already relies on to find these files.
const TARGET_W = 64;
const TARGET_H = 48;

const resolveFlagSvgUrl = (code) => {
    const el = document.createElement("span");
    el.className = `fi fi-${code}`;
    el.style.position = "fixed";
    el.style.visibility = "hidden";
    el.style.pointerEvents = "none";
    document.body.appendChild(el);
    const bg = getComputedStyle(el).backgroundImage || "";
    document.body.removeChild(el);
    const match = /url\(["']?([^"')]+)["']?\)/.exec(bg);
    return match ? match[1] : null;
};

const cache = new Map();

// Resolves a 2-letter country code to a small rasterized PNG data URL
// (or null on any failure - missing/unknown code, load error - so callers
// can just omit the icon instead of breaking the export).
export const getFlagDataUrl = (countryCode) => {
    if (!countryCode || typeof countryCode !== "string") return Promise.resolve(null);
    const code = countryCode.toLowerCase();
    if (cache.has(code)) return cache.get(code);

    const promise = new Promise((resolve) => {
        let url;
        try {
            url = resolveFlagSvgUrl(code);
        } catch (e) {
            url = null;
        }
        if (!url) return resolve(null);

        const img = new Image();
        img.onload = () => {
            try {
                const canvas = document.createElement("canvas");
                canvas.width = TARGET_W;
                canvas.height = TARGET_H;
                const ctx = canvas.getContext("2d");
                ctx.drawImage(img, 0, 0, TARGET_W, TARGET_H);
                resolve(canvas.toDataURL("image/png"));
            } catch (e) {
                resolve(null);
            }
        };
        img.onerror = () => resolve(null);
        img.src = url;
    });

    cache.set(code, promise);
    return promise;
};

// Pre-resolves every distinct code in `codes` in parallel and returns a
// { [code]: dataUrl|null } lookup - drawing is synchronous (pdf.text/
// addImage), so renderers need every flag already resolved before draw()
// runs rather than awaiting per-row.
export const preloadFlags = async (codes) => {
    const unique = Array.from(new Set((codes || []).filter(Boolean).map(c => String(c).toLowerCase())));
    const results = await Promise.all(unique.map(code => getFlagDataUrl(code)));
    const map = {};
    unique.forEach((code, i) => { map[code] = results[i]; });
    return map;
};
